/**
 * Top-level layout shell.
 *
 * Desktop (md+): a sidebar that sticks to the viewport (top-0, h-screen) so
 * the "New task" button stays anchored at the bottom regardless of page
 * scroll position.
 *
 * Mobile (<md): the sidebar is hidden by default. A hamburger button in the
 * page's top-left opens an off-canvas drawer that slides in from the left,
 * with a backdrop. The drawer auto-closes when the route changes.
 */
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Inbox, ListTodo, ShieldCheck, Tags, Plus, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const NAV = [
  { to: '/', label: 'Inbox', icon: Inbox, end: true },
  { to: '/tasks', label: 'Tasks', icon: ListTodo, end: false },
  { to: '/domains', label: 'Domains', icon: Tags, end: false },
  { to: '/audit', label: 'Audit', icon: ShieldCheck, end: false },
];

export function Layout(): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close the drawer whenever the route changes — the user just navigated.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the drawer is open on mobile so the page behind
  // doesn't scroll under the user's finger.
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [drawerOpen]);

  return (
    <div className="min-h-screen flex">
      {/* Desktop sidebar — sticky to viewport */}
      <aside className="hidden md:flex sticky top-0 h-screen w-56 shrink-0 border-r bg-card/50 px-4 py-6 flex-col gap-1">
        <SidebarContent />
      </aside>

      {/* Mobile drawer + backdrop */}
      <div
        className={cn(
          'md:hidden fixed inset-0 z-40 transition-opacity',
          drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        onClick={() => setDrawerOpen(false)}
        aria-hidden={!drawerOpen}
      >
        <div className="absolute inset-0 bg-black/40" />
      </div>
      <aside
        className={cn(
          'md:hidden fixed inset-y-0 left-0 z-50 w-64 border-r bg-card px-4 py-6 flex flex-col gap-1',
          'transition-transform duration-200 ease-out',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-hidden={!drawerOpen}
      >
        <button
          type="button"
          onClick={() => setDrawerOpen(false)}
          className="self-end p-1 -mr-1 rounded-md hover:bg-accent text-muted-foreground"
          aria-label="Close navigation"
        >
          <X className="h-5 w-5" />
        </button>
        <SidebarContent />
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        {/* Mobile top bar with hamburger */}
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 border-b bg-background/80 backdrop-blur">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="p-2 -ml-2 rounded-md hover:bg-accent text-muted-foreground"
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-semibold tracking-tight">AgentFlow</span>
        </div>
        <div className="px-4 md:px-8 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

/**
 * The actual nav links + new-task button. Shared between the desktop
 * sticky sidebar and the mobile drawer so the two surfaces never drift.
 */
function SidebarContent(): JSX.Element {
  return (
    <>
      <div className="px-3 mb-6">
        <h1 className="font-semibold text-lg tracking-tight">AgentFlow</h1>
        <p className="text-xs text-muted-foreground">v0.1 · phase 1</p>
      </div>
      <nav className="flex flex-col gap-1">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors',
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
              )
            }
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto">
        <NavLink
          to="/tasks/new"
          className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New task
        </NavLink>
      </div>
    </>
  );
}
